export const PERCENTAGE_PRESET = 'percentage-trading';
export const PERCENTAGE_LOOKBACKS = [5, 10, 20, 50, 100, 200];
export const hasPercentageLookback = (reference) => ['sma', 'rolling_high', 'rolling_low'].includes(reference);
export const PERCENTAGE_COMPARISON_EXAMPLE = Object.freeze({
  fall: '5', rise: '8', buyReference: 'previous_close', sellReference: 'entry_fill_price',
  buyWindow: '20', sellWindow: '20',
});

const indicatorKinds = { rolling_mean: 'sma', rolling_max: 'rolling_high', rolling_min: 'rolling_low' };

const percentage = (factor, direction) => {
  const number = Number(factor);
  if (!Number.isFinite(number)) return null;
  const raw = direction === 'fall' ? (1 - number) * 100 : (number - 1) * 100;
  const value = Math.round(raw * 100) / 100;
  if (value < 0.01 || value > 99.99) return null;
  return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const priceReference = (expression, indicators, side) => {
  if (expression?.target === 'bar.close' && expression.lag === 1) return { reference: 'previous_close', window: 20 };
  const id = expression?.target?.replace(/^indicator\./, '');
  const indicator = indicators.find((item) => item.id === id);
  const reference = indicatorKinds[indicator?.type];
  if (!id?.startsWith(`${side}_`) || !reference || indicator.source !== 'close'
    || indicator.offset !== 1 || !Number.isInteger(indicator.window)
    || indicator.window < 2 || indicator.window > 250) return null;
  return { reference, window: indicator.window };
};

export const percentageSettingsFromDefinition = (definition) => {
  const rules = definition?.canonical_configuration;
  if (rules?.entry_rule?.id !== 'entry.percentage_fall') return null;
  const indicators = rules.indicators || [];
  const buy = priceReference(rules.entry_rule.right?.left, indicators, 'buy');
  const fall = percentage(rules.entry_rule.right?.right?.value, 'fall');
  const technicalExit = rules.exit_policy?.technical_exit;
  const sell = rules.exit_policy?.take_profit?.enabled === true
    ? { reference: 'entry_fill_price', window: 20 }
    : technicalExit?.id === 'exit.percentage_rise'
      ? priceReference(technicalExit.right?.left, indicators, 'sell') : null;
  const rise = sell?.reference === 'entry_fill_price'
    ? String(rules.exit_policy.take_profit.percentage ?? '')
    : percentage(technicalExit?.right?.right?.value, 'rise');
  if (!buy || !sell || !fall || !rise) return null;
  return {
    name: definition.strategy_name,
    fall, rise, buyReference: buy.reference, sellReference: sell.reference,
    buyWindow: String(buy.window), sellWindow: String(sell.window),
  };
};

export const percentageReferenceText = (reference, window, side) => {
  if (reference === 'previous_close') return 'the previous completed close';
  if (reference === 'entry_fill_price' && side === 'sell') return 'the recorded virtual entry price';
  const kind = { sma: 'average', rolling_high: 'highest', rolling_low: 'lowest' }[reference];
  return `the ${kind} close in the previous ${window} completed trading sessions`;
};

export const percentageExplanation = ({ fall, rise, buyReference, sellReference, buyWindow, sellWindow }) => (
  `After a completed market day, consider buying when the close is at least ${fall}% below ${percentageReferenceText(buyReference, buyWindow, 'buy')}. While holding a virtual position, consider selling when the close is at least ${rise}% above ${percentageReferenceText(sellReference, sellWindow, 'sell')}. Each suggestion still needs your approval.`
);

export const percentageComparisonDetails = (definition = null) => {
  const settings = definition ? percentageSettingsFromDefinition(definition) : PERCENTAGE_COMPARISON_EXAMPLE;
  if (!settings) return null;
  return {
    configurationLabel: definition ? `Saved: ${definition.strategy_name}` : 'Example starting settings: 5% buy / 8% sell',
    buy: `Buy after a ${settings.fall}% decline from ${percentageReferenceText(settings.buyReference, settings.buyWindow, 'buy')}.`,
    sell: `Sell after a ${settings.rise}% rise from ${percentageReferenceText(settings.sellReference, settings.sellWindow, 'sell')}.`,
  };
};
